import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  doc, onSnapshot, collection, collectionGroup, query,
  where, getDocs, setDoc, serverTimestamp
} from 'firebase/firestore'
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, updateProfile
} from 'firebase/auth'
import { db, auth } from '../firebase/config'
import {
  User, Award, Heart, Music, TrendingUp, Calendar, Clock,
  Flame, LogOut, Mail, Lock, Eye, EyeOff, KeyRound,
  Star, CreditCard, CheckCircle, XCircle, AlertCircle,
  ArrowLeft, LogIn, UserPlus
} from 'lucide-react'
import { toast } from '../hooks/useToast'
import './RadioPageTV.css'
import './ProfilPageTV.css'



// ── Badge statut adhésion ────────────────────────────────
function SubStatusBadge({ status }) {
  const cfg = {
    active:    { color:'#34d399', bg:'rgba(52,211,153,0.12)',  border:'rgba(52,211,153,0.35)',  icon:<CheckCircle size={16}/>, label:'Active'     },
    completed: { color:'#34d399', bg:'rgba(52,211,153,0.12)',  border:'rgba(52,211,153,0.35)',  icon:<CheckCircle size={16}/>, label:'Actif'      },
    pending:   { color:'#ffd700', bg:'rgba(255,215,0,0.12)',   border:'rgba(255,215,0,0.35)',   icon:<AlertCircle size={16}/>, label:'En attente' },
    expired:   { color:'#ff6b6b', bg:'rgba(220,20,60,0.12)',   border:'rgba(220,20,60,0.35)',   icon:<XCircle     size={16}/>, label:'Expirée'    },
  }
  const c = cfg[status] || cfg.pending
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:8,
      padding:'6px 16px', borderRadius:30, fontSize:'0.9rem', fontWeight:700,
      color:c.color, background:c.bg, border:`1px solid ${c.border}`,
    }}>
      {c.icon}{c.label}
    </span>
  )
}

// ── Helpers ──────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return '—'
  const d = val.toDate?.() || new Date(val)
  return d.toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })
}

// ════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — ProfilePage TV
// ════════════════════════════════════════════════════════
function ProfilePageTV({ currentUser, onLogout }) {
  const navigate = useNavigate()
  const pageRef = useRef(null)
  const tabRefs = useRef([])
  const firstFocusRef = useRef(null)
  const emailRef = useRef(null)
  const passwordRef = useRef(null)
  const confirmRef = useRef(null)

  // ── Auth ─────────────────────────────────────────────
  const [authMode, setAuthMode]               = useState('login')
  const [email, setEmail]                     = useState('')
  const [password, setPassword]               = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName]         = useState('')
  const [showPassword, setShowPassword]       = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  // ── Profil ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('stats')
  const [userProfile, setUserProfile] = useState({
    totalListenTime:0, badges:[], streak:0, sessionsCount:0, recentTops:[]
  })
  const [userFavorites, setUserFavorites]       = useState([])
  const [loadingFavorites, setLoadingFavorites] = useState(true)

  // ── Adhésion ─────────────────────────────────────────
  const [adhesion, setAdhesion]         = useState(null)
  const [allAdhesions, setAllAdhesions] = useState([])
  const [loadingAdhesion, setLoadingAdhesion] = useState(true)

  const tabs = [
    { id:'stats',  icon:<TrendingUp size={24}/>, label:'Statistiques' },
    { id:'badges', icon:<Award      size={24}/>, label:'Badges'       },
    { id:'tops',   icon:<Music      size={24}/>, label:'Mes TOPs'     },
    { id:'favs',   icon:<Heart      size={24}/>, label:'Favoris'      },
    { id:'compte', icon:<CreditCard size={24}/>, label:'Adhésion'     },
  ]

  // ── Navigation télécommande TV ────────────────────────
  // Navigation pensée pour les télécommandes Smart TV :
  // ← / → : onglets quand le focus est sur les onglets
  // ↑ / ↓ : déplacement dans la page + scroll
  // OK / Entrée / Espace : activation du bouton sélectionné
  // HOME / END : début / fin de page
  // RETOUR / ESC : retour navigateur
  useEffect(() => {
    const isFormField = (el) =>
      el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)

    const getButtons = () => {
      if (!pageRef.current) return []
      return [...pageRef.current.querySelectorAll('button:not([disabled])')]
        .filter(el => {
          const r = el.getBoundingClientRect()
          const style = window.getComputedStyle(el)
          return r.width > 0 && r.height > 0 &&
            style.visibility !== 'hidden' &&
            style.display !== 'none'
        })
    }

    const focusNearest = (direction) => {
      const buttons = getButtons()
      if (!buttons.length) return

      const current = document.activeElement
      if (!current || !buttons.includes(current)) {
        ;(firstFocusRef.current || buttons[0])?.focus()
        return
      }

      const a = current.getBoundingClientRect()
      const ax = a.left + a.width / 2
      const ay = a.top + a.height / 2

      let best = null
      let bestScore = Infinity

      buttons.forEach(candidate => {
        if (candidate === current) return

        const r = candidate.getBoundingClientRect()
        const bx = r.left + r.width / 2
        const by = r.top + r.height / 2
        const dx = bx - ax
        const dy = by - ay

        const forward = direction === 'left' ? dx < -4
          : direction === 'right' ? dx > 4
          : direction === 'up' ? dy < -4
          : dy > 4

        if (!forward) return

        const primary = direction === 'left' || direction === 'right'
          ? Math.abs(dx)
          : Math.abs(dy)
        const secondary = direction === 'left' || direction === 'right'
          ? Math.abs(dy)
          : Math.abs(dx)

        // Favorise les éléments réellement alignés avec le focus.
        const score = primary + secondary * 2.2

        if (score < bestScore) {
          bestScore = score
          best = candidate
        }
      })

      if (best) {
        best.focus({ preventScroll: true })
        const r = best.getBoundingClientRect()
        const page = pageRef.current
        const margin = 40
        if (r.top < margin || r.bottom > window.innerHeight - margin) {
          page.scrollBy({
            top: r.top < margin ? r.top - margin : r.bottom - window.innerHeight + margin,
            behavior: 'smooth'
          })
        }
      } else if (pageRef.current) {
        pageRef.current.scrollBy({
          top: direction === 'up'
            ? -Math.max(260, window.innerHeight * .65)
            : Math.max(260, window.innerHeight * .65),
          behavior: 'smooth'
        })
      }
    }

    const onKeyDown = (e) => {
      const active = document.activeElement

      // Ne jamais casser la saisie clavier/télécommande dans les formulaires.
      if (isFormField(active)) return

      // Écran de connexion (pas encore de page profil) : les flèches et OK
      // sont gérées par la navigation TV globale (hooks/useTVRemote.js).
      // Avant, ce gestionnaire les bloquait sans rien trouver à focaliser.
      if (!pageRef.current) {
        if (['Escape', 'BrowserBack', 'GoBack', 'Backspace'].includes(e.key) || e.keyCode === 10009) {
          e.preventDefault()
          navigate('/')
        }
        return
      }

      // OK / Entrée / Select / Espace : active exactement le bouton focusé.
      if (['Enter', 'NumpadEnter', ' ', 'Select', 'OK'].includes(e.key)) {
        if (active?.tagName === 'BUTTON') {
          e.preventDefault()
          active.click()
        }
        return
      }

      // Retour de la télécommande.
      if (['Escape', 'BrowserBack', 'GoBack', 'Backspace'].includes(e.key)) {
        e.preventDefault()
        if (window.history.length > 1) navigate(-1)
        return
      }

      // Début / fin.
      if (e.key === 'Home') {
        e.preventDefault()
        pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        ;(firstFocusRef.current || getButtons()[0])?.focus({ preventScroll: true })
        return
      }

      if (e.key === 'End') {
        e.preventDefault()
        const page = pageRef.current
        page?.scrollTo({ top: page.scrollHeight, behavior: 'smooth' })
        const buttons = getButtons()
        buttons[buttons.length - 1]?.focus({ preventScroll: true })
        return
      }

      // Sur les onglets, ← / → restent une navigation horizontale précise.
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
          active?.closest('[data-tv-tabs="true"]')) {
        const current = tabs.findIndex(t => t.id === activeTab)
        const next = e.key === 'ArrowRight'
          ? Math.min(tabs.length - 1, current + 1)
          : Math.max(0, current - 1)

        if (next !== current) {
          e.preventDefault()
          setActiveTab(tabs[next].id)
          requestAnimationFrame(() => tabRefs.current[next]?.focus())
        }
        return
      }

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault()
        focusNearest(e.key.replace('Arrow', '').toLowerCase())
      }

      if (e.key === 'PageUp') {
        e.preventDefault()
        pageRef.current?.scrollBy({ top:-window.innerHeight * .8, behavior:'smooth' })
      }

      if (e.key === 'PageDown') {
        e.preventDefault()
        pageRef.current?.scrollBy({ top:window.innerHeight * .8, behavior:'smooth' })
      }
    }

    window.addEventListener('keydown', onKeyDown)

    // Place le focus sur le premier onglet à l'arrivée sur le profil.
    requestAnimationFrame(() => {
      const first = tabRefs.current[0]
      firstFocusRef.current = first
      if (document.activeElement === document.body) first?.focus()
    })

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeTab, navigate, currentUser])


  // ── Auth email ───────────────────────────────────────
  const handleAuth = async (e) => {
    e?.preventDefault?.()
    if (isAuthenticating) return
    // Validation manuelle : sur TV, pas de <form> (OK sur un champ
    // ouvrait la validation du navigateur au lieu du clavier).
    if (!email.trim() || !email.includes('@')) { toast.error('❌ Saisissez votre email'); emailRef.current?.focus(); return }
    if (authMode !== 'reset' && password.length < 6) { toast.error('❌ Mot de passe : 6 caractères minimum'); passwordRef.current?.focus(); return }
    setIsAuthenticating(true)
    try {
      if (authMode === 'reset') {
        await sendPasswordResetEmail(auth, email)
        toast.success('📧 Email envoyé !')
        setAuthMode('login'); setEmail(''); return
      }
      if (authMode === 'signup') {
        if (password !== confirmPassword) { toast.error('❌ Mots de passe différents'); return }
        if (password.length < 6)         { toast.error('❌ Minimum 6 caractères'); return }
        const cred = await createUserWithEmailAndPassword(auth, email, password)
        const name = displayName.trim() || email.split('@')[0]
        await updateProfile(cred.user, { displayName:name })
        await setDoc(doc(db,'users',cred.user.uid), {
          uid:cred.user.uid, email, displayName:name,
          totalListenTime:0, badges:[], streak:1, sessionsCount:0, recentTops:[],
          createdAt:serverTimestamp(), lastVisit:serverTimestamp()
        })
        toast.success('✅ Compte créé !')
        setEmail(''); setPassword(''); setConfirmPassword(''); setDisplayName(''); return
      }
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('✅ Connexion réussie !')
      setEmail(''); setPassword('')
    } catch(error) {
      const msgs = {
        'auth/user-not-found':      '❌ Email introuvable',
        'auth/wrong-password':      '❌ Mot de passe incorrect',
        'auth/invalid-credential':  '❌ Email ou mot de passe incorrect',
        'auth/email-already-in-use':'❌ Email déjà utilisé',
        'auth/weak-password':       '❌ Mot de passe trop faible',
        'auth/too-many-requests':   '❌ Trop de tentatives',
      }
      toast.error(msgs[error.code] || '❌ Erreur : ' + error.code)
    } finally { setIsAuthenticating(false) }
  }

  // ── Profil Firestore ─────────────────────────────────
  // Le vrai historique de temps d'écoute vit parfois sous
  // totalListeningTime / totalSecondsWatched (écrits par un autre
  // système) plutôt que totalListenTime — on prend le maximum,
  // comme dans ClassementPageTV.jsx.
  useEffect(() => {
    if (!currentUser) return
    return onSnapshot(doc(db,'users',currentUser.uid), snap => {
      if (!snap.exists()) return
      const d = snap.data()
      const totalListenTime = Math.max(
        Number(d.totalListenTime || 0),
        Number(d.totalListeningTime || 0),
        Number(d.totalSecondsWatched || 0),
        Number(d.stats?.totalSecondsWatched || 0)
      )
      setUserProfile({
        totalListenTime,
        badges:          d.badges          || [],
        streak:          d.streak || d.currentStreak || d.stats?.currentStreak || 0,
        sessionsCount:   d.sessionsCount || d.sessions || d.stats?.sessionsCount || 0,
        recentTops:      d.recentTops      || [],
      })
    })
  }, [currentUser])

  // ── Favoris ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return
    ;(async () => {
      setLoadingFavorites(true)
      try {
        const snap = await getDocs(query(
          collection(db, '100johnny', 'main', 'user_song_votes'),
          where('userId','==',currentUser.uid),
          where('vote','==','top')
        ))
        setUserFavorites(
          snap.docs.map(d => ({ id:d.id, ...d.data() }))
            .sort((a,b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))
        )
      } catch(e) { console.error(e) }
      finally { setLoadingFavorites(false) }
    })()
  }, [currentUser])

  // ── Adhésions ─────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) return
    ;(async () => {
      setLoadingAdhesion(true)
      let results = []

      // Tentative 1 : accès direct
      try {
        const ASSO_ID = '2ir61kP6b6SdKPbWM4UR'
        const snap = await getDocs(collection(db, '100johnny', 'main', 'associations', ASSO_ID, 'adhesions'))
        results = snap.docs
          .map(d => ({ id:d.id, ...d.data() }))
          .filter(d => d.userId === currentUser.uid)
      } catch(e) { console.warn('Direct:', e.message) }

      // Tentative 2 : collectionGroup userId
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db,'adhesions'), where('userId','==',currentUser.uid))
          )
          results = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        } catch(e) { console.warn('CollectionGroup:', e.message) }
      }

      // Tentative 3 : collectionGroup email
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db,'adhesions'), where('userEmail','==',currentUser.email))
          )
          results = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        } catch(e) { console.warn('Email:', e.message) }
      }

      results.sort((a,b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
      setAllAdhesions(results)
      setAdhesion(results.find(r => r.status === 'active') || null)
      setLoadingAdhesion(false)
    })()
  }, [currentUser?.uid])

  const formatTime = (s) => {
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60)
    return h > 0 ? `${h}h ${m}min` : `${m} min`
  }

  const allBadges = [
    { id:'first_listen',  name:'Première Écoute',  icon:'🎵', description:'Votre première chanson' },
    { id:'hour_listener', name:"1h d'Écoute",       icon:'⏰', description:"1 heure d'écoute"       },
    { id:'day_listener',  name:'Fan Quotidien',     icon:'📅', description:'24h cumulées'           },
    { id:'week_listener', name:'Mélomane',           icon:'🎸', description:"7 jours d'écoute"      },
    { id:'voter',         name:'Premier Vote',      icon:'👍', description:'Votre premier vote'     },
    { id:'top_voter',     name:'Critique Musical',  icon:'⭐', description:'10 votes'               },
    { id:'streak_7',      name:'Série de 7',        icon:'🔥', description:'7 jours consécutifs'   },
    { id:'night_owl',     name:'Oiseau de Nuit',    icon:'🦉', description:'Écoute après minuit'   },
  ]

  // ── FORMULAIRE AUTH (non connecté) ───────────────────
  // Même style que l'accueil TV (.tvh). Chaque élément est un vrai
  // bouton/champ empilé verticalement : ↑ ↓ passent de l'un à l'autre,
  // OK sur un champ ouvre le clavier de la TV.
  if (!currentUser) {
    const title = authMode === 'login' ? 'Connexion' : authMode === 'signup' ? 'Créer un compte' : 'Mot de passe oublié'
    const subtitle = authMode === 'login'
      ? 'Retrouvez vos statistiques, badges et favoris'
      : authMode === 'signup' ? 'Rejoignez la communauté 100% Johnny' : 'Recevez un lien de réinitialisation par email'

    // Touche « OK/Envoyer » du clavier TV sur le dernier champ : connexion
    // directe, seulement si tout est rempli. On ne bloque jamais la touche
    // (OK sur un champ vide doit ouvrir le clavier de la TV).
    const submitOnEnter = (e) => {
      if (e.key === 'Enter' && email.includes('@') && password.length >= 6) handleAuth()
    }

    const switchMode = (mode) => {
      setAuthMode(mode); setPassword(''); setConfirmPassword('')
      requestAnimationFrame(() => emailRef.current?.focus())
    }

    return (
      <div className="tvh tvl">
        <header className="tvl-head">
          <button type="button" className="tvh-btn" onClick={() => navigate('/')}>
            <ArrowLeft size="1.1em" /> Accueil
          </button>
          <div className="tvl-brand">
            <img src="/images/logo.png" alt="" className="tvh-logo" />
            <span>100% Johnny</span>
          </div>
        </header>

        <main className="tvl-main">
          <section className="tvl-intro">
            <span className="tvl-intro__icon">
              {authMode === 'reset' ? <KeyRound /> : authMode === 'signup' ? <UserPlus /> : <User />}
            </span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <ul className="tvl-tips">
              <li><span className="tvh-key">▲</span><span className="tvh-key">▼</span> Passer d'un champ à l'autre</li>
              <li><span className="tvh-key tvh-key--wide">OK</span> Ouvrir le clavier / valider</li>
              <li><span className="tvh-key tvh-key--wide">↩</span> Fermer le clavier</li>
            </ul>
          </section>

          <section className={`tvl-card ${authMode === 'signup' ? 'tvl-card--compact' : ''}`} aria-label={title}>
            {authMode === 'signup' && (
              <label className="tvl-field">
                <span>Pseudo (facultatif)</span>
                <span className="tvl-input">
                  <User />
                  <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                    placeholder="Votre nom de fan" enterKeyHint="next" />
                </span>
              </label>
            )}

            <label className="tvl-field">
              <span>Email</span>
              <span className="tvl-input">
                <Mail />
                <input ref={emailRef} type="email" inputMode="email" autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.com" data-tv-initial-focus=""
                  enterKeyHint={authMode === 'reset' ? 'done' : 'next'} />
              </span>
            </label>

            {authMode !== 'reset' && (
              <label className="tvl-field">
                <span>Mot de passe</span>
                <span className="tvl-input">
                  <Lock />
                  <input ref={passwordRef} type={showPassword ? 'text' : 'password'}
                    autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                    value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                    enterKeyHint={authMode === 'signup' ? 'next' : 'go'}
                    onKeyDown={authMode === 'login' ? submitOnEnter : undefined} />
                </span>
              </label>
            )}

            {authMode === 'signup' && (
              <label className="tvl-field">
                <span>Confirmer le mot de passe</span>
                <span className="tvl-input">
                  <Lock />
                  <input ref={confirmRef} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••"
                    enterKeyHint="go" onKeyDown={submitOnEnter} />
                </span>
              </label>
            )}

            {authMode !== 'reset' && (
              <div className="tvl-row">
                <button type="button" className="tvh-btn tvl-small" onClick={() => setShowPassword(v => !v)}>
                  {showPassword ? <EyeOff /> : <Eye />} {showPassword ? 'Masquer' : 'Afficher'} le mot de passe
                </button>
                {authMode === 'login' && (
                  <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('reset')}>
                    <KeyRound /> Mot de passe oublié ?
                  </button>
                )}
              </div>
            )}

            <button type="button" className="tvl-submit" onClick={handleAuth} disabled={isAuthenticating}>
              {isAuthenticating ? 'Un instant…'
                : authMode === 'login' ? <><LogIn /> Se connecter</>
                : authMode === 'signup' ? <><UserPlus /> Créer mon compte</>
                : <><Mail /> Envoyer le lien</>}
            </button>

            <div className="tvl-switch">
              {authMode === 'login' && (<>
                <span>Pas encore de compte ?</span>
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('signup')}>
                  <UserPlus /> Créer un compte
                </button>
              </>)}
              {authMode === 'signup' && (<>
                <span>Déjà inscrit ?</span>
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('login')}>
                  <LogIn /> Se connecter
                </button>
              </>)}
              {authMode === 'reset' && (
                <button type="button" className="tvh-btn tvl-small" onClick={() => switchMode('login')}>
                  <ArrowLeft /> Retour à la connexion
                </button>
              )}
            </div>
          </section>
        </main>
      </div>
    )
  }

  // ── PROFIL CONNECTÉ ──────────────────────────────────
  return (
    <div ref={pageRef} data-tv-own-nav="" className="profile-tv-page" style={{
      minHeight:'100vh',
      height:'100%',
      background:'radial-gradient(circle at 50% -10%,rgba(255,215,0,0.09),transparent 38%), linear-gradient(135deg,#070707,#121625 58%,#090909)',
      color:'#fff',
      padding:'clamp(20px,3vw,42px) clamp(24px,4vw,64px) 105px',
      fontFamily:'inherit',
      boxSizing:'border-box',
      overflowY:'auto',
      scrollBehavior:'smooth',
    }}>

      {/* ── Header profil ── */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        gap:24, marginBottom:'24px', padding:'22px 28px',
        background:'linear-gradient(135deg,rgba(255,215,0,0.08),rgba(255,255,255,0.035))',
        border:'1px solid rgba(255,215,0,0.22)',
        borderRadius:'24px',
        boxShadow:'0 18px 50px rgba(0,0,0,0.28)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'20px' }}>
          <div style={{ width:80, height:80, borderRadius:'50%', background:'linear-gradient(135deg,#ffd700,#ffed4e)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {currentUser.photoURL
              ? <img src={currentUser.photoURL} alt="avatar" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }}/>
              : <User size={42} color="#000"/>
            }
          </div>
          <div>
            <div style={{ color:'rgba(255,215,0,0.65)', fontSize:'0.72rem', fontWeight:800, letterSpacing:'2px', textTransform:'uppercase', marginBottom:5 }}>
              MON ESPACE FAN
            </div>
            <h1 style={{ fontSize:'clamp(1.8rem,3vw,2.5rem)', fontWeight:900, color:'#ffd700', margin:'0 0 6px', lineHeight:1.05 }}>
              {currentUser.displayName || currentUser.email?.split('@')[0]}
            </h1>
            <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'1rem', margin:'0 0 8px' }}>{currentUser.email}</p>
            {adhesion && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 14px', borderRadius:20, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.35)', color:'#ffd700', fontSize:'0.85rem', fontWeight:700 }}>
                <Star size={13} fill="#ffd700"/> MEMBRE ASSOCIATION
              </span>
            )}
          </div>
        </div>
        <button onClick={onLogout} style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 24px', borderRadius:14, cursor:'pointer', background:'rgba(220,20,60,0.15)', border:'1px solid rgba(220,20,60,0.4)', color:'#ff6b6b', fontWeight:700, fontSize:'1rem' }}>
          <LogOut size={22}/> Déconnexion
        </button>
      </div>

      {/* ── Onglets TV ── */}
      <div data-tv-tabs="true" style={{
        position:'sticky', top:0, zIndex:20,
        display:'flex', gap:12, marginBottom:'24px',
        overflowX:'auto', padding:'8px 0 12px',
        background:'linear-gradient(180deg,#0d1018 65%,rgba(13,16,24,0))',
      }}>
        {tabs.map((tab,index) => (
          <button
            key={tab.id}
            ref={el => { tabRefs.current[index] = el }}
            aria-selected={activeTab===tab.id}
            data-tv-focusable="true"
            onClick={() => setActiveTab(tab.id)}
            onFocus={() => setActiveTab(tab.id)}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'14px 28px', borderRadius:14, cursor:'pointer',
              border:'none', fontWeight:700, fontSize:'1rem',
              flexShrink:0, transition:'all 0.2s',
              background: activeTab===tab.id
                ? 'linear-gradient(135deg,#ffd700,#ffed4e)'
                : 'rgba(255,255,255,0.07)',
              color: activeTab===tab.id ? '#000' : 'rgba(255,255,255,0.7)',
              boxShadow: activeTab===tab.id ? '0 4px 20px rgba(255,215,0,0.3)' : 'none',
              transform: activeTab===tab.id ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* ── Résumé adhésion ── */}
      {!loadingAdhesion && adhesion && (
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:20,
          marginBottom:22, padding:'14px 18px',
          background:'rgba(52,211,153,0.07)',
          border:'1px solid rgba(52,211,153,0.22)',
          borderRadius:16,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:10, height:10, borderRadius:'50%', background:'#34d399', boxShadow:'0 0 12px rgba(52,211,153,0.8)' }}/>
            <div>
              <div style={{ fontWeight:900, color:'#34d399', fontSize:'0.85rem', letterSpacing:'0.6px' }}>ADHÉSION ACTIVE</div>
              <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.75rem', marginTop:2 }}>Votre carte membre est disponible dans l'onglet Adhésion.</div>
            </div>
          </div>
          <button onClick={() => setActiveTab('compte')} style={{ padding:'8px 14px', borderRadius:10, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.3)', color:'#ffd700', fontWeight:800, cursor:'pointer' }}>
            Voir mon adhésion →
          </button>
        </div>
      )}

      {/* ── Contenu ── */}

      {/* STATS */}
      {activeTab === 'stats' && (
        <div className="tv-stat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20 }}>
          {[
            { icon:<Clock    size={40} color="#00d9ff"/>, value:formatTime(userProfile.totalListenTime), label:"Temps d'écoute", color:'#00d9ff', bg:'rgba(0,217,255,0.08)',   border:'rgba(0,217,255,0.25)'   },
            { icon:<Flame    size={40} color="#ff1493"/>, value:userProfile.streak,                      label:'Jours de suite',  color:'#ff1493', bg:'rgba(255,20,147,0.08)',  border:'rgba(255,20,147,0.25)'  },
            { icon:<Calendar size={40} color="#ffd700"/>, value:userProfile.sessionsCount,               label:'Sessions',        color:'#ffd700', bg:'rgba(255,215,0,0.08)',   border:'rgba(255,215,0,0.25)'   },
            { icon:<Award    size={40} color="#00ff7f"/>, value:userProfile.badges.length,               label:'Badges',          color:'#00ff7f', bg:'rgba(0,255,127,0.08)',   border:'rgba(0,255,127,0.25)'   },
          ].map((s,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:20, padding:'28px 30px', borderRadius:18, background:s.bg, border:`2px solid ${s.border}` }}>
              {s.icon}
              <div>
                <div style={{ fontSize:'2.4rem', fontWeight:900, color:s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:'1rem', color:'rgba(255,255,255,0.55)', marginTop:4 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BADGES */}
      {activeTab === 'badges' && (
        <div className="tv-content-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
          {allBadges.map(badge => {
            const unlocked = userProfile.badges.includes(badge.id)
            return (
              <div key={badge.id} style={{ display:'flex', alignItems:'center', gap:16, padding:'20px 24px', borderRadius:16, background: unlocked ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${unlocked ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)'}`, opacity: unlocked ? 1 : 0.5 }}>
                <div style={{ fontSize:'2rem', flexShrink:0 }}>{badge.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color: unlocked ? '#ffd700' : '#fff', fontSize:'1rem' }}>{badge.name}</div>
                  <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.85rem', marginTop:2 }}>{badge.description}</div>
                </div>
                {unlocked && <Award size={22} color="#ffd700"/>}
              </div>
            )
          })}
        </div>
      )}

      {/* TOPS */}
      {activeTab === 'tops' && (
        userProfile.recentTops?.length > 0 ? (
          <div className="tv-list-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:14 }}>
            {userProfile.recentTops.map((top,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:18, padding:'18px 24px', borderRadius:16, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,215,0,0.1)' }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,215,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#ffd700', fontWeight:900, fontSize:'1.1rem', flexShrink:0 }}>#{i+1}</div>
                <div style={{ width:60, height:60, borderRadius:12, overflow:'hidden', background:'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {top.cover ? <img src={top.cover} alt={top.title} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <Music size={28} color="rgba(255,215,0,0.4)"/>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'#fff', fontSize:'1.05rem' }}>{top.title}</div>
                  <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.9rem', marginTop:3 }}>{top.artist}</div>
                </div>
                <Heart size={24} color="#ff1493" fill="#ff1493"/>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'rgba(255,255,255,0.35)' }}>
            <Music size={70} color="rgba(255,215,0,0.2)" style={{ marginBottom:20 }}/>
            <p style={{ fontSize:'1.2rem', marginBottom:8 }}>Aucun TOP enregistré</p>
            <p style={{ fontSize:'0.95rem' }}>Votez pour vos chansons préférées !</p>
          </div>
        )
      )}

      {/* FAVORIS */}
      {activeTab === 'favs' && (
        loadingFavorites ? (
          <div style={{ textAlign:'center', padding:'60px', color:'rgba(255,255,255,0.4)', fontSize:'1.1rem' }}>Chargement...</div>
        ) : userFavorites.length > 0 ? (
          <div className="tv-list-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:14 }}>
            {userFavorites.map(fav => (
              <div key={fav.id} style={{ display:'flex', alignItems:'center', gap:18, padding:'18px 24px', borderRadius:16, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,20,147,0.1)' }}>
                <div style={{ width:60, height:60, borderRadius:12, overflow:'hidden', background:'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {fav.cover ? <img src={fav.cover} alt={fav.songTitle} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <Music size={28} color="rgba(255,215,0,0.4)"/>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'#fff', fontSize:'1.05rem' }}>{fav.songTitle}</div>
                  <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.9rem', marginTop:3 }}>{fav.songArtist}</div>
                </div>
                <Heart size={24} color="#ff1493" fill="#ff1493"/>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'rgba(255,255,255,0.35)' }}>
            <Heart size={70} color="rgba(255,20,147,0.2)" style={{ marginBottom:20 }}/>
            <p style={{ fontSize:'1.2rem', marginBottom:8 }}>Aucun favori</p>
            <p style={{ fontSize:'0.95rem' }}>Votez TOP pour vos chansons favorites !</p>
          </div>
        )
      )}

      {/* ADHÉSION */}
      {activeTab === 'compte' && (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Titre section */}
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:'22px 26px', background:'rgba(255,215,0,0.06)', border:'1px solid rgba(255,215,0,0.2)', borderRadius:18 }}>
            <div style={{ width:52, height:52, borderRadius:14, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <CreditCard size={28} color="#ffd700"/>
            </div>
            <div>
              <div style={{ fontWeight:900, color:'#fff', fontSize:'1.2rem' }}>Mon Adhésion</div>
              <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.9rem', marginTop:2 }}>Association Radio 100% Johnny</div>
            </div>
          </div>

          {loadingAdhesion ? (
            <div style={{ textAlign:'center', padding:'50px', color:'rgba(255,255,255,0.4)', fontSize:'1.2rem' }}>
              ⏳ Chargement de votre adhésion...
            </div>

          ) : allAdhesions.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 30px', background:'rgba(255,255,255,0.03)', borderRadius:20, border:'1px dashed rgba(255,255,255,0.12)' }}>
              <div style={{ fontSize:'3.5rem', marginBottom:16 }}>🎸</div>
              <div style={{ fontWeight:800, color:'#fff', fontSize:'1.3rem', marginBottom:8 }}>Vous n'êtes pas encore membre</div>
              <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'1rem', marginBottom:28 }}>
                Rejoignez l'association pour soutenir la légende de Johnny
              </div>
              <button onClick={() => navigate('/abonnement')} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'16px 32px', borderRadius:50, background:'linear-gradient(135deg,#ffd700,#e6b800)', border:'none', color:'#000', fontWeight:900, fontSize:'1rem', cursor:'pointer', boxShadow:'0 8px 24px rgba(255,215,0,0.25)' }}>
                <CreditCard size={20}/> Adhérer — €20/an
              </button>
            </div>

          ) : (
            allAdhesions.map(sub => {
              const endDateObj = sub.endDate ? (sub.endDate.toDate?.() || new Date(sub.endDate)) : null
              const isExpired  = endDateObj ? endDateObj < new Date() : false
              const daysLeft   = endDateObj ? Math.ceil((endDateObj - new Date()) / 86400000) : null

              return (
                <div key={sub.id} style={{
                  background:'linear-gradient(135deg,rgba(255,215,0,0.06) 0%,rgba(255,255,255,0.02) 100%)',
                  border:`2px solid ${sub.status==='active' ? 'rgba(52,211,153,0.4)' : 'rgba(255,215,0,0.2)'}`,
                  borderRadius:22, padding:'28px 30px',
                  boxShadow: sub.status==='active' ? '0 4px 30px rgba(52,211,153,0.07)' : 'none',
                }}>
                  {/* Header */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                      <div style={{ width:56, height:56, borderRadius:14, background:'rgba(255,215,0,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.8rem' }}>🎸</div>
                      <div>
                        <div style={{ fontWeight:900, color:'#fff', fontSize:'1.1rem' }}>Adhésion 100% Johnny</div>
                        <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.85rem', marginTop:3 }}>{sub.type==='annuelle' ? 'Annuelle' : (sub.type || 'Annuelle')}</div>
                      </div>
                    </div>
                    <SubStatusBadge status={isExpired ? 'expired' : (sub.status || 'pending')}/>
                  </div>

                  {/* Infos grille */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px 20px', marginBottom:22 }}>
                    {[
                      { label:'N° Membre',  value:'JH·'+sub.id.substring(0,8).toUpperCase(), mono:true  },
                      { label:'Montant',    value: sub.totalPrice || sub.subscriptionPrice ? `€${sub.totalPrice || sub.subscriptionPrice}` : '—' },
                      { label:'Paiement',   value: sub.paymentMethod==='manual' ? 'Manuel' : sub.paymentMethod==='paypal' ? 'PayPal' : (sub.paymentMethod||'—') },
                      { label:'Début',      value:fmtDate(sub.startDate)  },
                      { label:'Expire le',  value:fmtDate(sub.endDate), warn:isExpired },
                      { label:'Créée le',   value:fmtDate(sub.createdAt) },
                    ].map(({ label, value, mono, warn }) => (
                      <div key={label} style={{ background:'rgba(255,255,255,0.03)', borderRadius:12, padding:'14px 16px' }}>
                        <div style={{ fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>{label}</div>
                        <div style={{ fontSize: mono?'0.88rem':'1rem', fontWeight:700, color: warn?'#ff6b6b':mono?'#ffd700':'#fff', fontFamily: mono?'monospace':'inherit' }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Barre progression */}
                  {sub.startDate && sub.endDate && (() => {
                    const start = (sub.startDate.toDate?.() || new Date(sub.startDate)).getTime()
                    const end   = (sub.endDate.toDate?.()   || new Date(sub.endDate)).getTime()
                    const pct   = Math.min(100, Math.max(0, Math.round(((Date.now()-start)/(end-start))*100)))
                    return (
                      <div>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                          <span style={{ fontSize:'0.85rem', color:'rgba(255,255,255,0.45)', fontWeight:600 }}>Validité écoulée</span>
                          <span style={{ fontSize:'0.85rem', color: daysLeft!==null && daysLeft<30?'#ff6b6b':'#10b981', fontWeight:700 }}>
                            {daysLeft!==null ? (daysLeft>0?`${daysLeft} jours restants`:'Expiré') : '—'}
                          </span>
                        </div>
                        <div style={{ height:8, borderRadius:4, background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${pct}%`, borderRadius:4, background: pct>80?'linear-gradient(90deg,#10b981,#ff6b6b)':'linear-gradient(90deg,#10b981,#ffd700)' }}/>
                        </div>
                      </div>
                    )
                  })()}

                  {/* Alerte expiration */}
                  {daysLeft !== null && daysLeft <= 30 && (
                    <div style={{ marginTop:18, padding:'14px 18px', borderRadius:12, background: isExpired?'rgba(220,20,60,0.08)':'rgba(255,215,0,0.07)', border:`1px solid ${isExpired?'rgba(220,20,60,0.3)':'rgba(255,215,0,0.3)'}` }}>
                      <span style={{ fontSize:'0.95rem', color: isExpired?'#ff6b6b':'#ffd700', fontWeight:700 }}>
                        {isExpired ? '⚠️ Adhésion expirée — Renouvelez sur 100pour100johnny.com' : `⏰ Expire dans ${daysLeft} jours — Renouvelez sur 100pour100johnny.com`}
                      </span>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* ── Navigation TV : flèches / titre / retour accueil ── */}
      <nav
        aria-label="Navigation TV"
        data-tv-bottom-nav="true"
        style={{
          position:'fixed',
          left:0,
          right:0,
          bottom:0,
          zIndex:100,
          height:40,
          display:'grid',
          gridTemplateColumns:'1fr auto 1fr',
          alignItems:'center',
          padding:'0 28px',
          background:'rgba(12,7,16,.96)',
          borderTop:'1px solid rgba(255,215,0,.16)',
          color:'#999',
          fontSize:'clamp(.72rem,1.05vw,.88rem)',
          boxSizing:'border-box',
        }}
      >
        <div style={{
          display:'flex',
          alignItems:'center',
          justifyContent:'flex-start',
          minWidth:0,
        }}>
          <button
            type="button"
            onClick={() => window.history.back()}
            data-tv-bottom-item="true"
            aria-label="Navigation précédente"
            style={{
              border:0,
              background:'transparent',
              color:'#999',
              padding:'4px 0',
              fontSize:'inherit',
              cursor:'pointer',
              whiteSpace:'nowrap',
            }}
          >
            ←→↑↓ Navigation  •  OK = Sélectionner
          </button>
        </div>

        <div style={{
          color:'#ffd700',
          fontWeight:800,
          letterSpacing:'.02em',
          whiteSpace:'nowrap',
          textAlign:'center',
        }}>
          100% JOHNNY
        </div>

        <div style={{
          display:'flex',
          alignItems:'center',
          justifyContent:'flex-end',
          minWidth:0,
        }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            data-tv-bottom-item="true"
            aria-label="Retour accueil"
            style={{
              border:0,
              background:'transparent',
              color:'#999',
              padding:'4px 0',
              fontSize:'inherit',
              cursor:'pointer',
              whiteSpace:'nowrap',
            }}
          >
            ↩ = Retour accueil
          </button>
        </div>
      </nav>

      <style>{`/* =========================================================
   PROFIL TV — FINITIONS
   ========================================================= */
.profile-tv-page button:focus-visible {
  outline: 4px solid rgba(255,215,0,.95) !important;
  outline-offset: 4px !important;
}
.profile-tv-page::-webkit-scrollbar { width: 10px; }
.profile-tv-page::-webkit-scrollbar-track { background: rgba(255,255,255,.03); }
.profile-tv-page::-webkit-scrollbar-thumb { background: rgba(255,215,0,.35); border-radius: 10px; }

@media (max-width: 1399px) {
  .profile-tv-page .tv-stat-grid,
  .profile-tv-page .tv-content-grid {
    grid-template-columns: repeat(2, minmax(0,1fr)) !important;
  }
}
@media (max-height: 800px) and (min-width: 1280px) {
  .profile-tv-page { padding-top: 16px !important; }
  .profile-tv-page .tv-stat-grid > div { padding: 20px !important; }
}`}</style>
    </div>
  )
}




export default ProfilePageTV